import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { authApi, setAccessTokenGetter } from '../api-client/index'
import type { PublicUser } from '../types/index'

// Shared auth store consumed by apps/shell and every module's Vue slice.
// Holds the access token in memory (never localStorage) and the refresh
// token in localStorage so the user survives a tab refresh.
export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem('creditify_rt') : null,
  )
  const user = ref<PublicUser | null>(null)
  const ready = ref(false)
  // Single-flight guard for initialize(): the refresh token is one-time-use, so
  // two concurrent initialize() calls (main.ts boot + router beforeEach guard on a
  // hard reload) would each POST /auth/refresh with the SAME token — the server
  // treats the 2nd as token reuse and panic-revokes the whole session, bouncing
  // the user to /login. Any caller that arrives while a refresh is in flight
  // awaits the same promise (one network call, one token rotation) instead of
  // firing its own.
  let initInFlight: Promise<void> | null = null

  // Wire the api-client to read tokens from this store.
  setAccessTokenGetter(() => accessToken.value)

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)

  function setRefresh(t: string | null) {
    refreshToken.value = t
    if (typeof localStorage !== 'undefined') {
      if (t) localStorage.setItem('creditify_rt', t)
      else localStorage.removeItem('creditify_rt')
    }
  }

  async function login(email: string, password: string) {
    const pair = await authApi.login(email, password)
    accessToken.value = pair.accessToken
    setRefresh(pair.refreshToken)
    user.value = pair.user
  }

  async function register(email: string, password: string, displayName?: string) {
    const pair = await authApi.register({ email, password, displayName })
    accessToken.value = pair.accessToken
    setRefresh(pair.refreshToken)
    user.value = pair.user
  }

  async function logout() {
    if (refreshToken.value) {
      try {
        await authApi.logout(refreshToken.value)
      } catch {
        /* best-effort; we clear local state regardless */
      }
    }
    accessToken.value = null
    setRefresh(null)
    user.value = null
  }

  async function runInitialize() {
    if (!refreshToken.value) {
      ready.value = true
      return
    }
    try {
      const pair = await authApi.refresh(refreshToken.value)
      accessToken.value = pair.accessToken
      setRefresh(pair.refreshToken)
      user.value = pair.user
    } catch {
      accessToken.value = null
      setRefresh(null)
      user.value = null
    }
    ready.value = true
  }

  function initialize(): Promise<void> {
    // Coalesce concurrent callers onto one in-flight refresh (see initInFlight).
    if (initInFlight) return initInFlight
    initInFlight = runInitialize().finally(() => {
      initInFlight = null
    })
    return initInFlight
  }

  async function refreshSelf() {
    const me = await authApi.me()
    user.value = me
    return me
  }

  return {
    accessToken,
    refreshToken,
    user,
    ready,
    isAuthenticated,
    login,
    register,
    logout,
    initialize,
    refreshSelf,
  }
})
